import { useHistory } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListItemText from '@mui/material/ListItemText';
import LogoutIcon from '@mui/icons-material/Logout';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import SavingsIcon from '@mui/icons-material/Savings';

import MuiDrawer from '@mui/material/Drawer'
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles'
import { AuthService } from '../../../services/AuthService'
import { Tooltip, Zoom } from '@mui/material'
import { WbSunny } from '@mui/icons-material'
import { useAuthLogoutMutation } from '../../../api-services/user/authenticationApi'
import { setNotification } from '../../../views-2/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'
import CircularProgress from '@mui/material/CircularProgress'
import React from 'react'
import BatchPredictionIcon from '@mui/icons-material/BatchPrediction'

interface SidebarNavigationComponentProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  DrawerHeader: any;
}

const SidebarNavigationComponent = (props: SidebarNavigationComponentProps) => {
  const { openDrawer, handleDrawerClose, DrawerHeader } = props

  const dispatch = useAppDispatch()
  const drawerWidth = 300

  const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: 'hidden'
  })

  const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(9)} + 1px)`
    }
  })

  const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme)
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme)
    })
  }))

  const theme = useTheme()

  const history = useHistory()
  const goTo = (location: string): void => {
    history.push(location)
  }


  const [apiLogout, { isLoading }] = useAuthLogoutMutation()

  const handleLogout = (): void => {
    apiLogout()
      .unwrap()
      .then(() => {
        AuthService.logout()
        goTo('/auth/login')
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <Drawer variant="permanent" open={openDrawer}>
      <DrawerHeader>
        <Grid container>
          <Grid item>
            <img alt={'Sun Exchange Logo'} src={'/icon.png'} style={{ height: 40, alignSelf: 'center' }}/>
          </Grid>
          <Grid item>
            <IconButton onClick={handleDrawerClose}>{theme.direction === 'rtl' ? <ChevronRightIcon/> :
              <ChevronLeftIcon/>}</IconButton>
          </Grid>
        </Grid>
      </DrawerHeader>
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/dashboard')}>
          <ListItemIcon>
            <Tooltip title="Dashboard" TransitionComponent={Zoom} arrow placement={openDrawer ? 'bottom' : 'right'}>
              <DashboardIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Dashboard'}/>
        </ListItem>
      </List>
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/customer-operations')}>
          <ListItemIcon>
            <Tooltip title="Customer Operations" TransitionComponent={Zoom} arrow
                     placement={openDrawer ? 'bottom' : 'right'}>
              <MedicalServicesOutlinedIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Customer Operations'}/>
        </ListItem>
      </List>
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/kyc-archive')}>
          <ListItemIcon>
            <Tooltip title="KYC Archive" TransitionComponent={Zoom} arrow
                     placement={openDrawer ? 'bottom' : 'right'}>
              <FolderSharedIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'KYC Archive'}/>
        </ListItem>
      </List>
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/finance-area')}>
          <ListItemIcon>
            <Tooltip title="Finance" TransitionComponent={Zoom} arrow placement={openDrawer ? 'bottom' : 'right'}>
              <SavingsIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Finance'}/>
        </ListItem>
      </List>
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/finance-area/settlements')}>
          <ListItemIcon>
            <Tooltip title="Settlements" TransitionComponent={Zoom} arrow placement={openDrawer ? 'bottom' : 'right'}>
              <BatchPredictionIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Settlements'}/>
        </ListItem>
      </List>
      <Divider />
      {/*<List>*/}
      {/*  <ListItem button onClick={() => goTo('/app/organisations')}>*/}
      {/*    <ListItemIcon>*/}
      {/*      <Tooltip title="Organisations" TransitionComponent={Zoom} arrow placement={openDrawer ? 'bottom' : 'right'}>*/}
      {/*        <HomeWorkIcon />*/}
      {/*      </Tooltip>*/}
      {/*    </ListItemIcon>*/}
      {/*    <ListItemText primary={'Organisations'} />*/}
      {/*  </ListItem>*/}
      {/*</List>*/}
      {/*<Divider />*/}
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/projects')}>
          <ListItemIcon>
            <Tooltip title="Solar Projects" TransitionComponent={Zoom} arrow
                     placement={openDrawer ? 'bottom' : 'right'}>
              <WbSunny/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Projects'}/>
        </ListItem>
      </List>
      <Divider/>
      <List>
        <ListItem button onClick={() => goTo('/app/solar-engineering-area')}>
          <ListItemIcon>
            <Tooltip title="Solar Engineering" TransitionComponent={Zoom} arrow
                     placement={openDrawer ? 'bottom' : 'right'}>
              <SolarPowerIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Solar Engineering'}/>
        </ListItem>
      </List>
      <Divider/>
      <List>
        <ListItem button onClick={() => handleLogout()}>
          <ListItemIcon>
            <Tooltip title="Sign out" TransitionComponent={Zoom} arrow placement={openDrawer ? 'bottom' : 'right'}>
              <LogoutIcon/>
            </Tooltip>
          </ListItemIcon>
          <ListItemText primary={'Sign out'}/>
          {isLoading && <CircularProgress size={22} sx={{ mb: 2 }}/>}
        </ListItem>
      </List>
    </Drawer>
  )
}

export default SidebarNavigationComponent
